"use client";

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import { AtcAccordion } from "@/components/meds/atc/atc-list/atc-accordion/atc-accordion";
import { AtcSelectionPanel } from "@/components/meds/atc/atc-list/atc-selection-panel/atc-selection-panel";
import {
  AtcKeyboardShortcutsHint,
  useAtcKeyboardShortcuts,
} from "./atc-keyboard-shortcuts";
import { AtcMedGroupsList } from "./atc-med-groups-list";
import { clientGet } from "@/service/client-api/client-api";
import { getAtcUrlPath, getAtcExpandedCodes, findAtcItem } from "./atc-utils";
import { AtcSearchBar } from "./atc-search-bar";
import { Switch } from "@/components/ui/switch";
import { AtcMedGroupsPagination } from "./atc-med-groups-pagination";
import { AtcMobileControls } from "./atc-mobile-controls";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";
import { trans } from "@/service/i18n/i18n";

const ITEMS_PER_PAGE = 20;

export const AtcList = ({
  items,
  initialCode,
}: {
  items: AtcItem[];
  initialCode?: string;
}) => {
  const [selectedAtc, setSelectedAtc] = useState<AtcItem | null>(() =>
    initialCode ? findAtcItem(items, initialCode) : null,
  );

  const initialExpandedIds = useMemo(
    () => (initialCode ? getAtcExpandedCodes(initialCode) : new Set<string>()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [compactView, setCompactView] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [paginatedMedGroups, setPaginatedMedGroups] = useState<
    MedGroupPreviewVM[]
  >([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMedGroups, setLoadingMedGroups] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { logView } = useContext(ResourceContext);

  const handleToggleCompactView = useCallback(
    () => setCompactView((v) => !v),
    [],
  );

  useAtcKeyboardShortcuts({
    onToggleCompactView: handleToggleCompactView,
  });

  // Debounce search input
  useEffect(() => {
    debounceTimeoutRef.current && clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);

    return () => {
      debounceTimeoutRef.current && clearTimeout(debounceTimeoutRef.current);
    };
  }, [search]);

  const fetchMedGroupsWithPagination = useCallback(
    async (atcId: string | number, pageNum: number, searchQuery: string) => {
      setLoadingMedGroups(true);
      try {
        const response = await clientGet("meds.med_groups.by_atc", {
          atcId,
          page: pageNum,
          search: searchQuery,
          limit: ITEMS_PER_PAGE,
        });
        setPaginatedMedGroups(response.items);
        setTotalItems(response.pagination.totalItems);
        setTotalPages(response.pagination.numPages);
      } catch {
        setPaginatedMedGroups([]);
        setTotalItems(0);
        setTotalPages(0);
      } finally {
        setLoadingMedGroups(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (selectedAtc) {
      fetchMedGroupsWithPagination(selectedAtc.code, page, debouncedSearch);
    } else {
      setPaginatedMedGroups([]);
      setTotalItems(0);
      setTotalPages(0);
    }
  }, [selectedAtc, page, debouncedSearch, fetchMedGroupsWithPagination]);

  const handleSelect = useCallback(
    (item: AtcItem) => {
      setSelectedAtc(item);
      setPage(1);
      setSearch("");
      setDebouncedSearch("");
      window.history.pushState(null, "", getAtcUrlPath(item.code));
      logEvent("atc_group_click", logView, {
        atcCode: item.code,
        atcTitle: item.titleText,
      });
    },
    [logView],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    },
    [],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    searchInputRef.current?.focus();
  }, []);

  const handlePreviousPage = useCallback(
    () => setPage((p) => Math.max(1, p - 1)),
    [],
  );
  const handleNextPage = useCallback(() => setPage((p) => p + 1), []);

  const handleDrawerLeafSelect = useCallback(() => setDrawerOpen(false), []);

  return (
    <div className="relative grid w-full grid-cols-1 gap-6 lg:grid-cols-[470px_1fr]">
      {/* Desktop: static left column */}
      <div className="hidden lg:block">
        <h2 className="mb-4 text-lg font-semibold">Klasyfikacja leków</h2>
        <AtcAccordion
          items={items}
          onSelect={handleSelect}
          initialExpandedIds={initialExpandedIds}
        />
      </div>

      {/* Right column / full-width on mobile */}
      <div className="flex min-h-[400px] flex-col">
        {/* Mobile drawer trigger + controls */}
        <AtcMobileControls
          ref={searchInputRef}
          items={items}
          selectedAtc={selectedAtc}
          drawerOpen={drawerOpen}
          onOpenChange={setDrawerOpen}
          onLeafSelect={handleDrawerLeafSelect}
          onSelect={handleSelect}
          initialExpandedIds={initialExpandedIds}
          search={search}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
          compactView={compactView}
          onCompactViewChange={setCompactView}
        />

        {/* Mobile pagination */}
        {selectedAtc && (
          <div className="mb-4 lg:hidden">
            <AtcMedGroupsPagination
              page={page}
              variant="mobile"
              totalPages={totalPages}
              totalItems={totalItems}
              onPrevious={handlePreviousPage}
              onNext={handleNextPage}
            />
          </div>
        )}

        {selectedAtc && (
          <div className="mb-4 hidden items-center justify-end gap-2 lg:flex lg:gap-4">
            {compactView !== undefined && setCompactView && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">
                  {compactView ? "Widok uproszczony" : "Widok szczegółowy"}
                </span>
                <Switch
                  checked={compactView}
                  onCheckedChange={setCompactView}
                />
              </div>
            )}
            <AtcSearchBar
              ref={searchInputRef}
              value={search}
              onChange={handleSearchChange}
              onClear={handleClearSearch}
            />
          </div>
        )}
        <div className="hidden justify-between gap-4 lg:flex">
          <AtcSelectionPanel selectedAtc={selectedAtc} />
          {selectedAtc && (
            <AtcMedGroupsPagination
              page={page}
              variant="compact"
              totalPages={totalPages}
              totalItems={totalItems}
              onPrevious={handlePreviousPage}
              onNext={handleNextPage}
            />
          )}
        </div>

        {/* Mobile placeholder */}
        {!selectedAtc && (
          <div className="lg:hidden">
            <AtcSelectionPanel selectedAtc={selectedAtc} />
          </div>
        )}

        {/* Med groups list */}
        {selectedAtc && (
          <AtcMedGroupsList
            loading={loadingMedGroups}
            paginatedMedGroups={paginatedMedGroups}
            compactView={compactView}
            search={search}
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
          />
        )}
      </div>

      {/* Keyboard shortcuts hint – desktop only */}
      <div className="sticky bottom-0 col-span-full hidden flex-wrap gap-3 border-t bg-white/90 px-3 py-2 text-xs text-gray-500 backdrop-blur-sm lg:flex">
        <AtcKeyboardShortcutsHint />
      </div>
    </div>
  );
};
